import {
  CaretLeft,
  CircleNotch,
  DotsThreeVertical,
  DownloadSimple,
  User,
} from "@phosphor-icons/react";

import LeadLabels from "common/components/LeadLabels";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useConversationExport from "../../../datahooks/useConversationExport";
import HeaderLoader from "./HeaderLoader";
import ParticipantInfo from "./ParticipantInfo";

import type { Conversation } from "../../../types";

interface InboxHeaderProps {
  conversation: Conversation;
  isLoading: boolean;
  resetActiveConversation: () => void;
  showLeadPreview: (leadId: string) => void;
}

export default function InboxHeader({
  conversation,
  isLoading,
  resetActiveConversation,
  showLeadPreview,
}: InboxHeaderProps) {
  const { exportConversation, isExportingConversation } =
    useConversationExport();
  const isDesktop = useTwBreakpoint("xl");

  const {
    participants,
    owner: accountId,
    conversation_urn: conversationUrn,
  } = conversation;

  const isGroup = conversation ? participants.length > 1 : false;

  if (isLoading && !conversation) {
    return <HeaderLoader />;
  }

  function handleExportConversation() {
    exportConversation({
      accountId,
      conversationUrn,
    });
  }

  return (
    <div className="flex lg:h-[60px] h-16 w-full items-center justify-center px-3 gap-2 lg:border-b lg:border-b-black-200">
      {/* Back to conversation button */}
      <Button
        className="lg:hidden"
        variant="ghost-black"
        intent="iconOnly"
        onClick={resetActiveConversation}
      >
        <CaretLeft />
      </Button>

      {/* Conversation Info */}
      <ParticipantInfo
        conversation={conversation}
        onParticipantSelect={showLeadPreview}
      />

      {/* Conversation actions */}
      <div className="ml-auto flex items-center gap-3">
        {/* Conversation tags management */}
        <RenderIf condition={isDesktop && !isGroup && participants[0].is_lead}>
          <div className="max-w-[520px]">
            <LeadLabels
              labels={participants[0].labels}
              profileId={participants[0].id}
              size="sm"
              align="end"
              maxLabels={4}
            />
          </div>

          <div className="h-10 w-px bg-black-200" />
        </RenderIf>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              intent="iconOnly"
              variant="secondary-black"
              onClick={(e) => e.stopPropagation()}
            >
              <DotsThreeVertical />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            align="end"
            className="z-aboveDialog"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuList>
              {/* Profile preview */}
              <RestrictedComponent visibleForPermissions={["leads_full"]}>
                <RenderIf condition={!isGroup}>
                  <DropdownMenuItem
                    onClick={() => showLeadPreview(participants[0].id)}
                  >
                    <User />
                    View profile
                  </DropdownMenuItem>
                </RenderIf>
              </RestrictedComponent>

              {/* Export conversation */}
              <DropdownMenuItem onClick={handleExportConversation}>
                {isExportingConversation ? (
                  <CircleNotch className="animate-spin" />
                ) : (
                  <DownloadSimple />
                )}
                Export conversation
              </DropdownMenuItem>
            </DropdownMenuList>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
